import { FileText, Layers, MailOpen, Calendar, Copy, Pencil } from "lucide-react";
import Drawer from "../../../components/ui/Drawer";
import Badge from "../../../components/ui/Badge";
import TemplateSkeleton from "./template-skeleton";
import { useAlert } from "../../../../hooks/useAlert";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-border bg-surface_alt/40 p-3 text-center">
      <Icon className="w-4 h-4 mx-auto mb-1.5 text-text_muted" />
      <div className="text-base font-bold text-text_primary leading-none">{value}</div>
      <div className="text-[11px] text-text_muted mt-1">{label}</div>
    </div>
  );
}

export default function TemplatePreviewDrawer({ template: t, open, onClose }) {
  const { showAlert } = useAlert();
  if (!t) return null;

  const use = () => {
    showAlert({ type: "success", message: `Template "${t.name}" applied to a new campaign.` });
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t.name}
      subtitle={`Subject: ${t.subject}`}
      icon={FileText}
      width={580}
      footer={
        <div className="flex items-center justify-between gap-2">
          <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium border border-border text-text_secondary hover:bg-surface_alt transition-colors">
            <Copy className="w-3.5 h-3.5" /> Duplicate
          </button>
          <div className="flex items-center gap-2">
            <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium border border-border text-text_secondary hover:bg-surface_alt transition-colors">
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
            <button
              onClick={use}
              className="px-5 py-2 rounded-xl text-sm font-semibold text-white"
              style={{ background: "linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)" }}
            >
              Use template
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="primary">{t.category.charAt(0).toUpperCase() + t.category.slice(1)}</Badge>
          {t.favorite && <Badge tone="warning" dot>Favorite</Badge>}
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Stat icon={Layers} label="Times used" value={t.uses} />
          <Stat icon={MailOpen} label="Avg open" value={`${t.openRate}%`} />
          <Stat icon={Calendar} label="Updated" value={t.updatedAt.split(",")[0]} />
        </div>

        {/* Rendered preview inside a faux email client frame */}
        <div className="rounded-xl border border-border overflow-hidden" style={{ background: "var(--color-surface-alt)" }}>
          <div className="flex items-center gap-1.5 px-3 py-2 border-b border-border">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--color-danger)" }} />
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--color-warning)" }} />
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--color-success)" }} />
            <span className="ml-2 text-[11px] text-text_muted truncate">{t.subject}</span>
          </div>
          <div className="p-5 max-h-[420px] overflow-y-auto">
            <TemplateSkeleton blocks={t.blocks} accent={t.accent} />
          </div>
        </div>

        <p className="text-xs text-text_muted leading-relaxed">{t.description}</p>
      </div>
    </Drawer>
  );
}
